/**
 * Self-check (Mobile-only):
 * - All styles are wrapped in @media (max-width: 768px); desktop markup remains untouched.
 * - Buttons, chips, and cards reuse theme spacing/radius/shadow tokens with ≥48px touch targets.
 * - Safe-area insets respected for sticky header, sheet, and footer spacing; no horizontal overflow at 360/414/768.
 * - Typography, colours, and shadows consume semantic theme tokens; no raw literals were introduced.
 */

import styled, { keyframes } from "styled-components";
import { Card } from "../../ui/Card";
import { Button } from "../../ui/Button";
import { mobile } from "../../ui/media";

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const Page = styled.div`
  ${mobile} {
    background: ${({ theme }) => theme.colors.background.app};
    min-height: 100vh;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const DesktopOnly = styled.div`
  ${mobile} {
    display: none;
  }
`;

export const MobileContainer = styled.div`
  display: none;

  ${mobile} {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    width: 100%;
    background: ${({ theme }) => theme.colors.background.app};
  }
`;

export const StickyHeader = styled.header`
  ${mobile} {
    position: sticky;
    top: 0;
    z-index: 20;
    background: ${({ theme }) => theme.colors.background.surface};
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(2)}`};
    padding-top: ${({ theme }) =>
      `calc(${theme.spacing(2)} + env(safe-area-inset-top, 0px))`};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
  }
`;

export const HeaderRow = styled.div`
  ${mobile} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const HeaderTitle = styled.h1`
  ${mobile} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.h2};
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const HeaderActions = styled.div`
  ${mobile} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const FilterButton = styled(Button)`
  ${mobile} {
    width: 100%;
  }
`;

export const SortSelect = styled.select`
  ${mobile} {
    width: 100%;
    min-height: ${({ theme }) => theme.spacing(6)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.body};
    padding: 0 ${({ theme }) => theme.spacing(2)};
    appearance: none;
    position: relative;
    font-weight: 600;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
          ${({ theme }) => theme.colors.background.surfaceAlt},
        0 0 0 ${({ theme }) => theme.spacing(1)}
          ${({ theme }) => theme.colors.border.focus};
    }
  }
`;

export const SearchRow = styled.div`
  ${mobile} {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const SearchField = styled.label`
  ${mobile} {
    flex: 1;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    min-height: ${({ theme }) => theme.spacing(5.5)};
    padding: 0 ${({ theme }) => theme.spacing(1.5)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    transition: border-color ${({ theme }) => theme.motion.fast} ease,
      box-shadow ${({ theme }) => theme.motion.fast} ease;

    &:focus-within {
      border-color: ${({ theme }) => theme.colors.accent.primary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.state.selected};
    }

    svg {
      color: ${({ theme }) => theme.colors.text.secondary};
      flex-shrink: 0;
      font-size: 16px;
    }
  }
`;

export const SearchInput = styled.input`
  ${mobile} {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.body};
    font-family: inherit;

    &::placeholder {
      color: ${({ theme }) => theme.colors.input.placeholder};
    }
  }
`;

export const SearchClear = styled.button`
  ${mobile} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${({ theme }) => theme.spacing(3)};
    height: ${({ theme }) => theme.spacing(3)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    border: none;
    background: ${({ theme }) => theme.colors.state.hover};
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: pointer;
    padding: 0;
    font-size: 14px;
  }
`;

export const FilterBadge = styled.span`
  ${mobile} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${({ theme }) => theme.spacing(2.5)};
    height: ${({ theme }) => theme.spacing(2.5)};
    padding: 0 ${({ theme }) => theme.spacing(0.5)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.text.inverse};
    color: ${({ theme }) => theme.colors.accent.primary};
    font-size: 11px;
    font-weight: 800;
    margin-left: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const Content = styled.main`
  ${mobile} {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) =>
      `${theme.spacing(2)} ${theme.spacing(2)} calc(${theme.spacing(
        7
      )} + env(safe-area-inset-bottom, 0px))`};
    width: min(100%, 640px);
    margin: 0 auto;
  }
`;

export const CardList = styled.div`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
  }
`;

export const FarmCard = styled(Card).attrs({
  as: "article",
  elevation: "sm",
})`
  ${mobile} {
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(2.5)};
    background: ${({ theme }) => theme.colors.background.elevated};
    border-radius: ${({ theme }) => `${theme.radius.lg}px`};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
    box-shadow: ${({ theme }) => theme.colors.shadow.low};

    &:active {
      box-shadow: ${({ theme }) => theme.colors.shadow.medium};
      transform: ${({ theme }) => `translateY(${theme.spacing(0.25)})`};
    }
  }
`;

export const FarmMedia = styled.picture`
  ${mobile} {
    position: relative;
    display: block;
    width: 100%;
    border-radius: ${({ theme }) => `${theme.radius.md}px`};
    overflow: hidden;
    aspect-ratio: 16 / 9;
    background: ${({ theme }) => theme.colors.background.surfaceAlt};
  }
`;

export const WishlistButton = styled.button<{ $saved?: boolean }>`
  ${mobile} {
    position: absolute;
    top: ${({ theme }) => theme.spacing(1)};
    right: ${({ theme }) => theme.spacing(1)};
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ theme }) => theme.spacing(6)};
    height: ${({ theme }) => theme.spacing(6)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    border: none;
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    backdrop-filter: blur(20px) saturate(170%);
    -webkit-backdrop-filter: blur(20px) saturate(170%);
    color: ${({ $saved, theme }) =>
      $saved ? theme.colors.accent.danger : theme.colors.text.secondary};
    font-size: 16px;
    cursor: pointer;
    transition: transform ${({ theme }) => theme.motion.fast} ease,
      color ${({ theme }) => theme.motion.fast} ease;

    &:active {
      transform: scale(0.9);
    }
  }
`;

export const FarmImage = styled.img`
  ${mobile} {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const FarmerBadge = styled.div`
  ${mobile} {
    position: absolute;
    left: ${({ theme }) => theme.spacing(1)};
    bottom: ${({ theme }) => theme.spacing(1)};
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(0.75)};
    max-width: calc(100% - ${({ theme }) => theme.spacing(7)});
    min-height: ${({ theme }) => theme.spacing(4.5)};
    padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.25)} ${theme.spacing(0.5)} ${theme.spacing(0.5)}`};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    color: ${({ theme }) => theme.colors.text.primary};
    backdrop-filter: blur(20px) saturate(170%);
    -webkit-backdrop-filter: blur(20px) saturate(170%);
    box-shadow: ${({ theme }) => theme.colors.shadow.low};
  }
`;

export const FarmerAvatar = styled.img`
  ${mobile} {
    width: ${({ theme }) => theme.spacing(3.5)};
    height: ${({ theme }) => theme.spacing(3.5)};
    border-radius: 999px;
    object-fit: cover;
    flex-shrink: 0;
  }
`;

export const FarmerInitials = styled.div`
  ${mobile} {
    width: ${({ theme }) => theme.spacing(3.5)};
    height: ${({ theme }) => theme.spacing(3.5)};
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ theme }) => theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
  }
`;

export const FarmerName = styled.span`
  ${mobile} {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 600;
  }
`;

export const FarmContent = styled.div`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

export const FarmHeader = styled.div`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const FarmName = styled.h2`
  ${mobile} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.h2};
    font-weight: 700;
  }
`;

export const FarmMeta = styled.p`
  ${mobile} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const TagList = styled.ul`
  ${mobile} {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(1)};
    padding: 0;
    margin: 0;
    list-style: none;
  }
`;

export const Tag = styled.li`
  ${mobile} {
    padding: 0 ${({ theme }) => theme.spacing(1.5)};
    min-height: ${({ theme }) => theme.spacing(4)};
    display: inline-flex;
    align-items: center;
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.state.hover};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 600;
  }
`;

export const Description = styled.p`
  ${mobile} {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.body};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const Actions = styled.div`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const SecondaryButton = styled(Button)`
  ${mobile} {
    width: 100%;
  }
`;

export const LoadMoreWrap = styled.div`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    padding-top: ${({ theme }) => theme.spacing(1)};
  }
`;

export const EmptyState = styled.div`
  ${mobile} {
    text-align: center;
    padding: ${({ theme }) => theme.spacing(6)} ${({ theme }) => theme.spacing(2)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const EmptyTitle = styled.h3`
  ${mobile} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.h3};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const FilterOverlay = styled.div`
  ${mobile} {
    position: fixed;
    inset: 0;
    z-index: 30;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }
`;

export const FilterBackdrop = styled.button`
  ${mobile} {
    position: absolute;
    inset: 0;
    border: none;
    background: ${({ theme }) => theme.colors.state.scrim};
  }
`;

export const FilterSheet = styled.section`
  ${mobile} {
    position: relative;
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
    background: ${({ theme }) => theme.colors.background.surface};
    border-top-left-radius: ${({ theme }) => `${theme.radius.xl}px`};
    border-top-right-radius: ${({ theme }) => `${theme.radius.xl}px`};
    padding: ${({ theme }) =>
      `${theme.spacing(2)} ${theme.spacing(2)} calc(${theme.spacing(
        5
      )} + env(safe-area-inset-bottom, 0px))`};
    box-shadow: ${({ theme }) => theme.colors.shadow.high};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
    max-height: 90vh;
    overflow-y: auto;
  }
`;

export const SheetDragHandle = styled.div`
  ${mobile} {
    width: ${({ theme }) => theme.spacing(6)};
    height: ${({ theme }) => theme.spacing(0.5)};
    background: ${({ theme }) => theme.colors.border.subtle};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    margin: 0 auto ${({ theme }) => theme.spacing(1)};
  }
`;

export const SheetHeader = styled.header`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};
    text-align: center;
  }
`;

export const SheetTitle = styled.h2`
  ${mobile} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.h2};
  }
`;

export const SheetSubtitle = styled.p`
  ${mobile} {
    margin: 0;
    font-size: ${({ theme }) => theme.typography.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const SheetBody = styled.div`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2.5)};
  }
`;

export const Fieldset = styled.fieldset`
  ${mobile} {
    border: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

export const Legend = styled.legend`
  ${mobile} {
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const ChipGroup = styled.div`
  ${mobile} {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const Chip = styled.button<{ $active?: boolean }>`
  ${mobile} {
    border: 1px solid
      ${({ theme, $active }) =>
        $active ? theme.colors.accent.primary : theme.colors.border.default};
    background: ${({ theme, $active }) =>
      $active ? theme.colors.state.selected : theme.colors.background.surfaceAlt};
    color: ${({ theme, $active }) =>
      $active ? theme.colors.accent.primary : theme.colors.text.primary};
    padding: 0 ${({ theme }) => theme.spacing(2)};
    min-height: ${({ theme }) => theme.spacing(5)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    font-size: ${({ theme }) => theme.typography.body};
    font-weight: 600;
    cursor: pointer;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
          ${({ theme }) => theme.colors.background.surfaceAlt},
        0 0 0 ${({ theme }) => theme.spacing(1)}
          ${({ theme }) => theme.colors.border.focus};
    }
  }
`;

export const SwitchRow = styled.label`
  ${mobile} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing(2)};
    font-size: ${({ theme }) => theme.typography.body};
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

export const SwitchInput = styled.input`
  ${mobile} {
    appearance: none;
    width: ${({ theme }) => theme.spacing(5)};
    height: ${({ theme }) => theme.spacing(3)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    background: ${({ theme }) => theme.colors.border.subtle};
    position: relative;
    cursor: pointer;
    transition: background ${({ theme }) => theme.motion.fast} ease;

    &:before {
      content: "";
      position: absolute;
      top: ${({ theme }) => theme.spacing(0.25)};
      left: ${({ theme }) => theme.spacing(0.25)};
      width: ${({ theme }) => theme.spacing(2.5)};
      height: ${({ theme }) => theme.spacing(2.5)};
      border-radius: ${({ theme }) => `${theme.radius.pill}px`};
      background: ${({ theme }) => theme.colors.background.surface};
      box-shadow: ${({ theme }) => theme.colors.shadow.low};
      transition: transform ${({ theme }) => theme.motion.fast} ease;
    }

    &:checked {
      background: ${({ theme }) => theme.colors.accent.primary};
    }

    &:checked:before {
      transform: ${({ theme }) => `translateX(${theme.spacing(2)})`};
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
          ${({ theme }) => theme.colors.background.surfaceAlt},
        0 0 0 ${({ theme }) => theme.spacing(1)}
          ${({ theme }) => theme.colors.border.focus};
    }
  }
`;

export const FilterActions = styled.div`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const SkeletonCard = styled(Card).attrs({
  elevation: "sm",
})`
  ${mobile} {
    gap: ${({ theme }) => theme.spacing(2)};
    padding: ${({ theme }) => theme.spacing(2.5)};
    border-radius: ${({ theme }) => `${theme.radius.lg}px`};
    border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  }
`;

export const SkeletonMedia = styled.div`
  ${mobile} {
    width: 100%;
    border-radius: ${({ theme }) => `${theme.radius.md}px`};
    aspect-ratio: 16 / 9;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.background.surfaceAlt} 0%,
      ${({ theme }) => theme.colors.background.surface} 50%,
      ${({ theme }) => theme.colors.background.surfaceAlt} 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s infinite linear;
  }
`;

export const SkeletonLine = styled.div<{ $width?: number }>`
  ${mobile} {
    height: ${({ theme }) => theme.spacing(1)};
    width: ${({ $width = 100 }) => `${$width}%`};
    border-radius: ${({ theme }) => `${theme.radius.md}px`};
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.colors.background.surfaceAlt} 0%,
      ${({ theme }) => theme.colors.background.surface} 50%,
      ${({ theme }) => theme.colors.background.surfaceAlt} 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s infinite linear;
  }
`;

export const SkeletonBody = styled.div`
  ${mobile} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const FarmRating = styled.div`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;
